/**
 * Consent Management Service
 * 
 * Manages user consent preferences for GDPR compliance
 * Article 7 - Conditions for Consent
 */

import { 
  doc, 
  getDoc, 
  setDoc, 
  serverTimestamp,
  Timestamp,
  collection,
  addDoc,
  query,
  where,
  getDocs,
  orderBy,
  limit,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { logger } from './logger';

export type ConsentType = 
  | 'analytics'
  | 'marketing'
  | 'email_access'
  | 'cloud_backup'
  | 'location'
  | 'notifications';

export type ConsentStatus = 'granted' | 'denied' | 'withdrawn';

export type ConsentPreference = {
  type: ConsentType;
  status: ConsentStatus;
  grantedAt?: Date;
  withdrawnAt?: Date;
  version?: string; // Privacy policy version when consent was given
};

export type ConsentPreferences = {
  [key in ConsentType]?: ConsentPreference;
};

export type ConsentHistoryEntry = {
  type: ConsentType;
  status: ConsentStatus;
  timestamp: Date;
  privacyPolicyVersion?: string;
  method: 'in-app' | 'email' | 'web';
  ipAddress?: string; // For web platform
};

/**
 * Get current consent preferences for a user
 */
export const getConsentPreferences = async (userId: string): Promise<ConsentPreferences> => {
  try {
    const consentRef = doc(db, 'users', userId, 'consentPreferences', 'current');
    const snapshot = await getDoc(consentRef);
    
    if (!snapshot.exists()) {
      return {};
    }
    
    const data = snapshot.data();
    const preferences: ConsentPreferences = {};
    
    // Convert Firestore timestamps to Dates
    for (const [key, value] of Object.entries(data)) {
      if (value && typeof value === 'object' && 'status' in value) {
        const pref = value as any;
        preferences[key as ConsentType] = {
          type: pref.type || key,
          status: pref.status,
          version: pref.version,
          grantedAt: pref.grantedAt?.toDate ? pref.grantedAt.toDate() : (pref.grantedAt instanceof Date ? pref.grantedAt : undefined),
          withdrawnAt: pref.withdrawnAt?.toDate ? pref.withdrawnAt.toDate() : (pref.withdrawnAt instanceof Date ? pref.withdrawnAt : undefined),
        } as ConsentPreference;
      }
    }
    
    return preferences;
  } catch (error) {
    logger.error('Error fetching consent preferences', error);
    return {};
  }
};

/**
 * Update consent preference for a specific type
 */
export const updateConsentPreference = async (
  userId: string,
  type: ConsentType,
  status: ConsentStatus,
  privacyPolicyVersion?: string
): Promise<void> => {
  try {
    const consentRef = doc(db, 'users', userId, 'consentPreferences', 'current');
    const currentDoc = await getDoc(consentRef);
    
    const now = Timestamp.now();
    const preferenceData: any = {
      type,
      status,
      version: privacyPolicyVersion,
    };
    
    if (status === 'granted') {
      preferenceData.grantedAt = now;
    } else if (status === 'withdrawn') {
      preferenceData.withdrawnAt = now;
      // Keep the original grantedAt if it exists
      if (currentDoc.exists()) {
        const currentData = currentDoc.data();
        const existingPreference = currentData[type];
        if (existingPreference?.grantedAt) {
          // If it's already a Timestamp, keep it; otherwise convert
          preferenceData.grantedAt = existingPreference.grantedAt instanceof Timestamp 
            ? existingPreference.grantedAt 
            : Timestamp.fromDate(existingPreference.grantedAt.toDate());
        }
      }
    }
    
    // Update current preferences
    await setDoc(
      consentRef,
      {
        [type]: preferenceData,
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
    
    // Record in consent history
    await recordConsentHistory(userId, {
      type,
      status,
      timestamp: now.toDate(),
      privacyPolicyVersion,
      method: 'in-app',
    });
    
    logger.info(`Consent preference updated`, { userId, type, status });
  } catch (error) {
    logger.error('Error updating consent preference', error);
    throw error;
  }
};

/**
 * Record consent history entry
 */
export const recordConsentHistory = async (
  userId: string,
  entry: ConsentHistoryEntry
): Promise<void> => {
  try {
    await addDoc(
      collection(db, 'users', userId, 'consentHistory'),
      {
        ...entry,
        timestamp: serverTimestamp(),
      }
    );
  } catch (error) {
    logger.error('Error recording consent history', error);
    // Don't throw - history is not critical for functionality
  }
};

/**
 * Get consent history for a user
 */
export const getConsentHistory = async (
  userId: string,
  limitCount: number = 50
): Promise<ConsentHistoryEntry[]> => {
  try {
    const historyRef = collection(db, 'users', userId, 'consentHistory');
    const q = query(historyRef, orderBy('timestamp', 'desc'), limit(limitCount));
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        ...data,
        timestamp: data.timestamp?.toDate() || new Date(),
      } as ConsentHistoryEntry;
    });
  } catch (error) {
    logger.error('Error fetching consent history', error);
    return [];
  }
};

/**
 * Withdraw consent for a specific type
 */
export const withdrawConsent = async (
  userId: string,
  type: ConsentType
): Promise<void> => {
  await updateConsentPreference(userId, type, 'withdrawn');
};

/**
 * Grant consent for a specific type
 */
export const grantConsent = async (
  userId: string,
  type: ConsentType,
  privacyPolicyVersion?: string
): Promise<void> => {
  await updateConsentPreference(userId, type, 'granted', privacyPolicyVersion);
};

/**
 * Check if user has granted consent for a specific type
 */
export const hasConsent = async (
  userId: string,
  type: ConsentType
): Promise<boolean> => {
  const preferences = await getConsentPreferences(userId);
  const preference = preferences[type];
  return preference?.status === 'granted';
};

/**
 * Get current privacy policy version
 * This should match the version in privacy-policy.tsx
 */
export const getCurrentPrivacyPolicyVersion = (): string => {
  // This should be updated when privacy policy changes
  return '2025-01';
};

